import {FC, memo, useMemo} from 'react';
import clsx from 'clsx';
import {format, parseISO} from 'date-fns';
import {enCA, frCA} from 'date-fns/locale';
import {useTranslation} from 'react-i18next';

import CheckIcon from '@/assets/images/icon-check.svg?react';
import CrossIcon from '@/assets/images/icon-close.svg?react';
import Spinner from '@/components/spinner';
import {useSavingTransitionState} from './use-saving-transition-state';

type SavingIndicatorProps = {
  hasUnsavedChanges?: boolean;
  isSaving?: boolean;
  lastSaved: string;
  hasFailed?: boolean;
};

export type SavingIndicatorState = 'idle' | 'saving' | 'saved' | 'error';

export const SavingIndicator: FC<SavingIndicatorProps> = memo(function SavingIndicator({
  hasUnsavedChanges,
  isSaving,
  lastSaved,
  hasFailed,
}) {
  const {t, i18n} = useTranslation();
  const state = useSavingTransitionState(!!isSaving, !!hasFailed);

  const lastSavedText = useMemo(() => {
    const dateFormatted = format(parseISO(lastSaved), 'yyyy-MM-dd HH:mm', {
      locale: i18n.language === 'fr' ? frCA : enCA,
    });
    return t('components.saving-indicator.last-saved', {date: dateFormatted});
  }, [i18n.language, lastSaved, t]);

  return (
    <div className="size-6">
      {state === 'saving' && <Spinner thin className="size-full" />}
      {(state === 'saved' || state === 'idle' || state === 'error') && (
        <div
          title={lastSavedText}
          className={clsx(
            'flex items-center justify-center rounded-full transition-colors duration-1000',
            state === 'saved' && 'bg-violet-300 p-1',
            state === 'idle' && 'bg-greige p-1',
            hasUnsavedChanges && 'bg-white p-1',
            state === 'error' && 'bg-red-300 p-2',
          )}
        >
          {state === 'error' ? (
            <CrossIcon className="size-2 fill-white stroke-white" />
          ) : (
            <CheckIcon className="size-4 fill-white stroke-white" />
          )}
        </div>
      )}
    </div>
  );
});
